import {
    Bold,
    CodeAlt,
    Italic,
    ListOl,
    ListUl,
    Strikethrough,
    Terminal,
} from "@styled-icons/boxicons-regular";
import { QuoteAltLeft } from "@styled-icons/boxicons-solid/QuoteAltLeft";
import styled from "styled-components/macro";
import { memo } from "preact/compat";
import { useCallback, useEffect, useState } from "preact/hooks";

import { useOutsideClick } from "../../../lib/hooks/use-outside-click";

import Tooltip from "../Tooltip";

enum FormatText {
    BOLD = "BOLD",
    ITALICS = "ITALICS",
    STRIKE = "STRIKE",
    CODE = "CODE",
    CODE_BLOCK = "CODE_BLOCK",
    H1 = "H1",
    H2 = "H2",
    H3 = "H3",
    UNORDERED_LIST = "UNORDERED_LIST",
    ORDERED_LIST = "ORDERED_LIST",
    QUOTE = "QUOTE",
}

type SelectionRange = {
    start: number;
    end: number;
} | null;

type UseTextFormattingReturn = {
    isShowFormattingToolbar: boolean;
    applyFormatting: (type: FormatText) => void;
    closeFormattingToolbar: () => void;
    onSelect: (e: Event) => void;
};

const getBoldText = (text: string): string => `**${text}**`;
const getItalicsText = (text: string): string => `*${text}*`;
const getStrikeText = (text: string): string => `~~${text}~~`;
const getCodeText = (text: string): string => `\`${text}\``;
const getCodeBlockText = (text: string): string => `\`\`\`\n${text}\n\`\`\``;

const getHeadingText = (level: number, text: string): string => {
    const hashes = "#".repeat(level);
    return `${hashes} ${text}`;
};

const getUnorderedListText = (text: string): string => {
    return text
        .split("\n")
        .map((line) => (line.trim() ? `- ${line}` : line))
        .join("\n");
};

const getOrderedListText = (text: string): string => {
    return text
        .split("\n")
        .map((line, i) => (line.trim() ? `${i + 1}. ${line}` : line))
        .join("\n");
};

const getQuoteText = (text: string): string => {
    return text
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
};

const getFormatConfig = (type: FormatText, text: string): string => {
    const formatMap: Record<FormatText, string> = {
        [FormatText.BOLD]: getBoldText(text),
        [FormatText.ITALICS]: getItalicsText(text),
        [FormatText.STRIKE]: getStrikeText(text),
        [FormatText.CODE]: getCodeText(text),
        [FormatText.CODE_BLOCK]: getCodeBlockText(text),
        [FormatText.H1]: getHeadingText(1, text),
        [FormatText.H2]: getHeadingText(2, text),
        [FormatText.H3]: getHeadingText(3, text),
        [FormatText.UNORDERED_LIST]: getUnorderedListText(text),
        [FormatText.ORDERED_LIST]: getOrderedListText(text),
        [FormatText.QUOTE]: getQuoteText(text),
    };

    return formatMap[type];
};

const FORMATTING_TOOLBAR_CLOSE_DELAY = 100;

export const useTextFormatting = (
    content = "",
    setContent: (setContent: string) => void,
): UseTextFormattingReturn => {
    const [isShowFormattingToolbar, setIsShowFormattingToolbar] =
        useState(false);
    const [selectionRange, setSelectionRange] = useState<SelectionRange>(null);

    const closeFormattingToolbar = useCallback(() => {
        setIsShowFormattingToolbar(false);
        setSelectionRange(null);
    }, []);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const handleSelectionChange = () => {
            const selection = document.getSelection();

            if (!selection) return;

            const hasSelection = selection?.toString().length > 0;

            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            if (!hasSelection) {
                timeoutId = setTimeout(
                    closeFormattingToolbar,
                    FORMATTING_TOOLBAR_CLOSE_DELAY,
                );
            }
        };

        document.addEventListener("selectionchange", handleSelectionChange);

        return () => {
            document.removeEventListener(
                "selectionchange",
                handleSelectionChange,
            );
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [closeFormattingToolbar]);

    if (!content.length) {
        closeFormattingToolbar();
    }

    const applyFormatting = useCallback(
        (type: FormatText) => {
            if (!selectionRange) return;

            const { start, end } = selectionRange;
            const selectedText = content.substring(start, end);

            if (!selectedText.trim()) return;

            const formatConfig = getFormatConfig(type, selectedText);
            if (!formatConfig) return;

            const newContent = [
                content.substring(0, start),
                formatConfig,
                content.substring(end),
            ].join("");

            setContent(newContent);
            closeFormattingToolbar();
        },
        [selectionRange, content, setContent, closeFormattingToolbar],
    );

    const onSelect = useCallback(
        (e: Event) => {
            const textarea = e.currentTarget as HTMLTextAreaElement;

            if (document.activeElement !== textarea) {
                closeFormattingToolbar();
                return;
            }

            const { selectionStart, selectionEnd } = textarea;

            if (selectionStart === null || selectionEnd === null) return;

            const selectedText = textarea.value.substring(
                selectionStart,
                selectionEnd,
            );

            if (!selectedText.trim()) {
                return;
            }

            if (selectionStart !== selectionEnd) {
                setSelectionRange({ start: selectionStart, end: selectionEnd });
                setIsShowFormattingToolbar(true);
            } else {
                closeFormattingToolbar();
            }
        },
        [closeFormattingToolbar],
    );

    return {
        applyFormatting,
        isShowFormattingToolbar,
        closeFormattingToolbar,
        onSelect,
    };
};

const OverlayBar = styled.div`
    display: flex;
    position: absolute;
    justify-self: end;
    align-self: end;
    align-content: center;
    justify-content: center;
    transform: translateY(-100%);
    z-index: 0;
    transition: box-shadow 0.1s ease-out;
    border-radius: 5px;
    background: var(--primary-header);
    border: 1px solid var(--background);
    left: 0;
    top: 0;

    &:hover {
        box-shadow: rgb(0 0 0 / 20%) 0 2px 10px;
    }
`;

const Entry = styled.div`
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    transition: 0.2s ease background-color;
    color: var(--secondary-foreground);

    &:hover {
        background-color: var(--secondary-header);
        color: var(--foreground);
    }

    &:focus {
        border-radius: var(--border-radius);
        box-shadow: 0 0 0 2.5pt var(--accent);
    }

    &:active {
        svg {
            transform: translateY(1px);
        }
    }
`;

interface FormattingToolbarProps {
    onFormat: (type: FormatText) => void;
    onClose: () => void;
}

export const FormattingToolbar = memo(
    ({ onFormat, onClose }: FormattingToolbarProps) => {
        const containerRef = useOutsideClick(onClose);

        return (
            <OverlayBar ref={containerRef}>
                <Tooltip content="Bold">
                    <Entry onClick={() => onFormat(FormatText.BOLD)}>
                        <Bold size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Italics">
                    <Entry onClick={() => onFormat(FormatText.ITALICS)}>
                        <Italic size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Strikethrough">
                    <Entry onClick={() => onFormat(FormatText.STRIKE)}>
                        <Strikethrough size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Code">
                    <Entry onClick={() => onFormat(FormatText.CODE)}>
                        <CodeAlt size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Code Block">
                    <Entry onClick={() => onFormat(FormatText.CODE_BLOCK)}>
                        <Terminal size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="H1">
                    <Entry onClick={() => onFormat(FormatText.H1)}>H1</Entry>
                </Tooltip>
                <Tooltip content="H2">
                    <Entry onClick={() => onFormat(FormatText.H2)}>H2</Entry>
                </Tooltip>
                <Tooltip content="H3">
                    <Entry onClick={() => onFormat(FormatText.H3)}>H3</Entry>
                </Tooltip>
                <Tooltip content="Ordered List">
                    <Entry onClick={() => onFormat(FormatText.ORDERED_LIST)}>
                        <ListOl size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Unordered List">
                    <Entry onClick={() => onFormat(FormatText.UNORDERED_LIST)}>
                        <ListUl size="20" />
                    </Entry>
                </Tooltip>
                <Tooltip content="Quote">
                    <Entry onClick={() => onFormat(FormatText.QUOTE)}>
                        <QuoteAltLeft size="20" />
                    </Entry>
                </Tooltip>
            </OverlayBar>
        );
    },
);
